// Deck Recipes — public, SEO-indexed catalog of DeckTemplate documents.
//
// Header (sidebar + back + “Open deck”): `DeckRecipesNavigationOptions.jsx`.
//
// Layout mirrors /explore and /tasks: discovery headline, then
// `CatalogBrowseShell` with a horizontal toolbar (category dropdown).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, ScrollView, StyleSheet, Pressable, Platform } from 'react-native';
import { Redirect, useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuth from '@/hooks/useRequireAuth';
import { deckTemplatesApi, deckTemplateCategoriesApi } from '@/lib/api';
import { useDeckStore, useAuthStore } from '@/store';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import DeckRecipeCard from '@/components/Deck/DeckRecipeCard';
import CatalogBrowseShell from '@/components/Catalog/CatalogBrowseShell';
import CatalogCategoryDropdown from '@/components/Dropdowns/CatalogCategoryDropdown';
import CatalogMobileToolbar from '@/components/Catalog/CatalogMobileToolbar';
import { DECK_RECIPES_RAIL_FAVORITES_SLUG } from '@/components/Deck/DeckRecipesCatalogRail';
import CatalogDiscoveryHero from '@/components/Catalog/CatalogDiscoveryHero';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import {
  buildCategoryLookupMaps,
  enrichTemplateCategory,
  formatRecipeCategoryLabel,
  recipeCategorySlug,
} from '@/utils/deckRecipeCategory';

const isWeb = Platform.OS === 'web';
// The phone apps don't ship Deck, so its recipe catalog has nowhere to lead.
const IS_TOUCH_NATIVE = Platform.OS === 'ios' || Platform.OS === 'android';

/**
 * Long-form FAQ block surfaced both on-page and in the FAQPage JSON-LD.
 * Source of truth for the schema.org FAQ structured data.
 */
const RECIPES_INDEX_FAQ = [
  { q: 'What exactly is a deck recipe?', a: 'A pre-configured Gab AI Deck layout — the column structure, default model bindings, labels, and an SEO-friendly description — saved as a reusable template. Cloning copies the layout into your workspace; you can swap any model, add or remove columns, and save your version as a new recipe.' },
  { q: 'Are deck recipes free?', a: 'Yes. The catalog is free to browse and free to clone — no recipe is paywalled. Individual model and tool runs inside the deck consume credits according to your Gab AI plan, exactly the same as anywhere else in the app.' },
  { q: 'How is this different from prompt libraries?', a: 'Prompt libraries are static text. Deck recipes are full multi-column workflows: each column runs an independent model or tool, columns reference each other, and the whole layout persists across sessions. You get something to clone, not just to copy-paste.' },
  { q: 'Can I customise a recipe after I clone it?', a: 'Yes. Once cloned, the recipe becomes your deck. Rename it, change column model bindings, add chat or tool columns, drag to reorder, resize columns, or duplicate winners. Up to 12 columns per deck.' },
  { q: 'How are recipes ranked and organised?', a: 'Featured recipes show first, then we group by category — music video, podcast, brand design, ad creative, e-commerce, social media, research, dev tools, and more. Each category page lives at /decks/recipes/category/<slug> and includes its own intro copy and FAQ.' },
  { q: 'Do new recipes ship regularly?', a: 'Yes. We add recipes as new models land (Imagen 4, Kling 3, Wan 2.7, Seedance 2.0, MiniMax Music 2.6, etc.) or whenever a customer-favourite workflow is worth promoting to the catalog. Bookmark this page or save your own recipes for the workflows you reuse most.' },
  { q: 'Can I share a recipe with my team?', a: 'Yes. Every recipe has its own permanent URL at /decks/recipes/<slug>. Send the link to a teammate; one click on “Use this recipe” spins up a deck under their account with the same column layout.' },
  { q: 'What if my use case isn’t covered?', a: 'Start blank from /deck and build the layout you want. Anything you wire up can be saved as your own recipe — and the workflows that prove themselves often graduate into this catalog.' },
];

const BASE_STRUCTURED_DATA_GRAPH = [
  {
    '@type': 'CollectionPage',
    name: 'Gab AI Deck Recipes — Free Multi-Column AI Workflow Templates',
    description: 'AI workflow templates for Gab AI Deck: compare LLMs, chain image and video models, add music and voice columns, and run tools in parallel. Each recipe includes FAQs, HowTo steps, and one-click clone.',
    url: 'https://gab.ai/decks/recipes',
    isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: 'https://gab.ai' },
    inLanguage: 'en-US',
  },
  {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://gab.ai' },
      { '@type': 'ListItem', position: 2, name: 'Deck', item: 'https://gab.ai/deck' },
      { '@type': 'ListItem', position: 3, name: 'Deck Recipes', item: 'https://gab.ai/decks/recipes' },
    ],
  },
  {
    '@type': 'FAQPage',
    mainEntity: RECIPES_INDEX_FAQ.map((row) => ({
      '@type': 'Question',
      name: row.q,
      acceptedAnswer: { '@type': 'Answer', text: row.a },
    })),
  },
];

export default function DeckRecipesIndexRoute() {
  if (IS_TOUCH_NATIVE) return <Redirect href="/" />;
  return <DeckRecipesIndex />;
}

function DeckRecipesIndex() {
  const { colors } = useTheme();
  const { isXS, isSM, isMD, windowWidth } = useResponsiveness();
  const router = useRouter();
  const favoriteDeckTemplateIds = useAuthStore(state => state.user?.favoriteDeckTemplateIds);
  const requireAuth = useRequireAuth();
  const createDeck = useDeckStore(state => state.actions.createDeck);

  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busySlug, setBusySlug] = useState(null);
  /** `null` = all categories */
  const [selectedCategorySlug, setSelectedCategorySlug] = useState(null);
  const useRecipeBusyRef = useRef(false);

  const pushCreatedDeck = useCallback((deckSlug) => {
    if (deckSlug) {
      router.push({ pathname: '/deck', params: { d: deckSlug } });
    }
  }, [router]);


  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [tplRes, catRes] = await Promise.all([
          deckTemplatesApi.list(),
          deckTemplateCategoriesApi.list().catch(() => ({ categories: [] })),
        ]);
        if (!alive) return;
        setTemplates(Array.isArray(tplRes?.templates) ? tplRes.templates : []);
        setCategories(Array.isArray(catRes?.categories) ? catRes.categories : []);
      } catch (err) {
        if (!alive) return;
        setError(err?.message || 'Failed to load recipes');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const categoryMaps = useMemo(() => buildCategoryLookupMaps(categories), [categories]);

  const enrichedTemplates = useMemo(
    () => templates.map((t) => enrichTemplateCategory(t, categoryMaps)),
    [templates, categoryMaps],
  );

  const categoryPills = useMemo(() => {
    const rows = (categories || []).filter((c) => c?.slug && c.flags?.isEnabled !== false);
    if (rows.length) {
      return [...rows].sort(
        (a, b) => (a.order ?? 999) - (b.order ?? 999) || String(a.name || '').localeCompare(String(b.name || '')),
      );
    }
    const slugs = [...new Set(enrichedTemplates.map((t) => recipeCategorySlug(t)))].sort();
    return slugs.map((slug) => ({
      slug,
      name: slug.replace(/-/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()),
    }));
  }, [categories, enrichedTemplates]);

  const favoriteIdSet = useMemo(
    () => new Set((favoriteDeckTemplateIds || []).map(String)),
    [favoriteDeckTemplateIds],
  );

  const deckCategoryTopItems = useMemo(() => [
    {
      key: 'all',
      label: 'All recipes',
      icon: 'grid-2',
      tint: '#3B82F6',
      isActive: !selectedCategorySlug,
      onSelect: () => setSelectedCategorySlug(null),
    },
    {
      key: 'favorites',
      label: 'Favorites',
      icon: 'heart',
      tint: '#EF4444',
      isActive: selectedCategorySlug === DECK_RECIPES_RAIL_FAVORITES_SLUG,
      iconSolid: selectedCategorySlug === DECK_RECIPES_RAIL_FAVORITES_SLUG,
      onSelect: () => setSelectedCategorySlug(
        selectedCategorySlug === DECK_RECIPES_RAIL_FAVORITES_SLUG
          ? null
          : DECK_RECIPES_RAIL_FAVORITES_SLUG,
      ),
    },
  ], [selectedCategorySlug]);

  const deckActiveCategorySlug = useMemo(() => {
    if (!selectedCategorySlug || selectedCategorySlug === DECK_RECIPES_RAIL_FAVORITES_SLUG) {
      return null;
    }
    return selectedCategorySlug;
  }, [selectedCategorySlug]);

  const deckCategoryDropdown = useMemo(() => (
    <CatalogCategoryDropdown
      categories={categoryPills}
      activeCategorySlug={deckActiveCategorySlug}
      onChangeCategory={(slug) => {
        if (!slug) {
          setSelectedCategorySlug(null);
          return;
        }
        const key = String(slug).toLowerCase();
        setSelectedCategorySlug(deckActiveCategorySlug === key ? null : key);
      }}
      topItems={deckCategoryTopItems}
      showAllOption={false}
      defaultTriggerLabel="Categories"
      allOptionLabel="All recipes"
      getCategoryTint={() => colors.accent_color_primary}
    />
  ), [categoryPills, deckActiveCategorySlug, deckCategoryTopItems, colors.accent_color_primary]);

  const deckBrowseToolbar = useMemo(() => ({
    hideViewToggle: true,
    trailing: deckCategoryDropdown,
  }), [deckCategoryDropdown]);

  const deckMobileToolbar = useMemo(() => (
    <CatalogMobileToolbar
      showViewToggle={false}
      chipSlots={[
        <CatalogCategoryDropdown
          key="deck-cat"
          variant="toolbarChip"
          categories={categoryPills}
          activeCategorySlug={deckActiveCategorySlug}
          onChangeCategory={(slug) => {
        if (!slug) {
          setSelectedCategorySlug(null);
          return;
        }
        const key = String(slug).toLowerCase();
        setSelectedCategorySlug(deckActiveCategorySlug === key ? null : key);
      }}
          topItems={deckCategoryTopItems}
          showAllOption={false}
          defaultTriggerLabel="Categories"
          allOptionLabel="All recipes"
          getCategoryTint={() => colors.accent_color_primary}
        />,
      ]}
    />
  ), [categoryPills, deckActiveCategorySlug, deckCategoryTopItems, colors.accent_color_primary]);

  const showDeckCatalogChrome = !loading && !error && templates.length > 0;

  const templatesInCategory = useMemo(() => {
    if (selectedCategorySlug === DECK_RECIPES_RAIL_FAVORITES_SLUG) {
      return enrichedTemplates.filter((t) => t?.id != null && favoriteIdSet.has(String(t.id)));
    }
    if (!selectedCategorySlug) return enrichedTemplates;
    const key = String(selectedCategorySlug).toLowerCase();
    return enrichedTemplates.filter((t) => recipeCategorySlug(t) === key);
  }, [enrichedTemplates, selectedCategorySlug, favoriteIdSet]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of templatesInCategory) {
      const key = recipeCategorySlug(t);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    return Array.from(map.entries())
      .filter(([, list]) => list.length > 0)
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [templatesInCategory]);

  const featured = useMemo(
    () => templatesInCategory.filter(t => t.flags?.isFeatured).slice(0, 6),
    [templatesInCategory],
  );

  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  const { cardGridGap, cardColWidth } = useMemo(() => {
    if (isXS) return { cardGridGap: 12, cardColWidth: Math.min(300, Math.max(248, windowWidth - 48)) };
    if (isSM) return { cardGridGap: 14, cardColWidth: 252 };
    if (isMD) return { cardGridGap: 14, cardColWidth: 264 };
    return { cardGridGap: 16, cardColWidth: 272 };
  }, [isXS, isSM, isMD, windowWidth]);

  const cardRowStyle = useMemo(() => {
    if (Platform.OS !== 'web') {
      return [styles.cardRow, { gap: cardGridGap }];
    }
    return [
      styles.cardRow,
      styles.cardRowWeb,
      {
        gap: cardGridGap,
        // Equal-width columns that grow to fill the row; short last rows keep
        // the same track width as full rows (auto-fill leaves empty tracks).
        gridTemplateColumns: `repeat(auto-fill, minmax(${Math.round(cardColWidth)}px, 1fr))`,
      },
    ];
  }, [cardGridGap, cardColWidth, styles]);

  const handleUseRecipe = useCallback(async (template) => {
    if (!template?.slug || useRecipeBusyRef.current) return;
    // Creating a deck is owner-only and writes to the user's library —
    // gate anonymous visitors via the unified auth CTA. They can still
    // browse the recipe detail directly via the card body.
    const ran = requireAuth(() => true, {
      source: 'deck_recipes_use_recipe',
      title: `Sign in to use ${template?.name || 'this recipe'}`,
      subtitle: 'Recipes turn into decks in your library so you can keep iterating on them.',
      icon: 'layer-group',
    });
    if (ran !== true) return;
    useRecipeBusyRef.current = true;
    setBusySlug(template.slug);
    try {
      const deckSlug = await createDeck({ name: template.name, templateSlug: template.slug });
      pushCreatedDeck(deckSlug);
    } finally {
      useRecipeBusyRef.current = false;
      setBusySlug(null);
    }
  }, [requireAuth, createDeck, pushCreatedDeck]);

  const handleCardPress = useCallback((template) => {
    router.push(`/decks/recipes/${template.slug}`);
  }, [router]);

  /**
   * Dynamic JSON-LD: include an `ItemList` of the live catalog so search
   * crawlers see every recipe URL on the index page (sitelinks help).
   */
  const structuredData = useMemo(() => {
    const itemList = enrichedTemplates.length > 0
      ? [{
        '@type': 'ItemList',
        name: 'Gab AI Deck Recipe Catalog',
        numberOfItems: enrichedTemplates.length,
        itemListElement: enrichedTemplates.slice(0, 100).map((t, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: `https://gab.ai/decks/recipes/${t.slug}`,
          name: t.name,
        })),
      }]
      : [];
    return { '@context': 'https://schema.org', '@graph': [...BASE_STRUCTURED_DATA_GRAPH, ...itemList] };
  }, [enrichedTemplates]);

  const mainBody = (
    <>
      {loading && (
        <View style={styles.loading}>
          <Icon name="spinner" size={18} color={colors.text_color_tertiary} />
        </View>
      )}

      {!!error && !loading && (
        <View style={[styles.errorBox, { backgroundColor: colors.solid_color_block, borderColor: colors.border_color_secondary }]}>
          <Text size="s" color="secondary">{error}</Text>
        </View>
      )}

      {!loading && !error && featured.length > 0 && (
        <>
          <Text size="xs" weight="bold" color="tertiary" style={[styles.sectionLabel, isXS && styles.sectionLabelTight]}>
            FEATURED RECIPES
          </Text>
          <View style={cardRowStyle}>
            {featured.map(t => (
              <DeckRecipeCard
                key={t.id || t.slug}
                template={t}
                categoryFallbackSlug={recipeCategorySlug(t)}
                layout={{ isXS, isSM, isMD }}
                onPress={() => handleCardPress(t)}
                onUse={() => handleUseRecipe(t)}
                busy={busySlug === t.slug}
                featured
              />
            ))}
          </View>
        </>
      )}

      {!loading && !error && templatesInCategory.length === 0 && templates.length > 0 && (
        <View style={[styles.emptyBox, { backgroundColor: colors.solid_color_block, borderColor: colors.border_color_secondary }]}>
          <Icon name="layer-group" size={18} color={colors.text_color_tertiary} />
          <Text size="m" weight="bold" color="primary" style={{ marginTop: 12, textAlign: 'center' }}>
            {selectedCategorySlug === DECK_RECIPES_RAIL_FAVORITES_SLUG
              ? 'No saved favorites in this catalog'
              : (selectedCategorySlug
                ? 'No recipes in this category'
                : 'No recipes found')}
          </Text>
          <Text size="s" color="secondary" style={{ marginTop: 4, textAlign: 'center' }}>
            {selectedCategorySlug
              ? (selectedCategorySlug === DECK_RECIPES_RAIL_FAVORITES_SLUG
                ? 'Tap the heart on a recipe card to save it here.'
                : 'Try another category or clear the filter.')
              : 'Try again later.'}
          </Text>
          <Pressable
            onPress={() => { setSearchQuery(''); setSelectedCategorySlug(null); }}
            style={({ hovered }) => [
              styles.emptyCta,
              { borderColor: colors.border_color_secondary, backgroundColor: hovered ? colors.solid_color_hover : 'transparent' },
            ]}
          >
            <Icon name="xmark" size={10} color={colors.text_color_secondary} />
            <Text size="xs" weight="bold" color="secondary">Clear filters</Text>
          </Pressable>
        </View>
      )}

      {!loading && !error && grouped.map(([cat, list]) => (
        <View key={cat} style={styles.section}>
          <View style={[styles.sectionHeaderRow, isXS && styles.sectionHeaderRowTight]}>
            <Text size="xs" weight="bold" color="tertiary" style={styles.sectionTitle}>
              {formatRecipeCategoryLabel(list[0], cat)}
            </Text>
            <Pressable
              onPress={() => router.push(`/decks/recipes/category/${encodeURIComponent(cat)}`)}
              hitSlop={8}
              style={({ hovered }) => [
                styles.showAllBtn,
                isWeb && hovered ? { opacity: 0.85 } : null,
              ]}
            >
              <Text size="xs" weight="bold" color='primary'>Show all</Text>
              <View style={{ marginLeft: 4 }}>
                <Icon name="arrow-right" size={10} color={colors.text_color_primary} />
              </View>
            </Pressable>
          </View>
          <View style={cardRowStyle}>
            {list.map(t => (
              <DeckRecipeCard
                key={t.id || t.slug}
                template={t}
                categoryFallbackSlug={cat}
                layout={{ isXS, isSM, isMD }}
                onPress={() => handleCardPress(t)}
                onUse={() => handleUseRecipe(t)}
                busy={busySlug === t.slug}
              />
            ))}
          </View>
        </View>
      ))}

      {/* ─────────────── SEO long-form sections ───────────────
          Static evergreen content for /decks/recipes that helps rank for
          "AI workflow templates", "multi-column AI", "deck recipes", etc.
          Suppressed when the user is mid-search/filter so it doesn't push
          card results below the fold. */}
      {!loading && !error && !selectedCategorySlug && enrichedTemplates.length > 0 && (
        <View style={styles.seoWrap}>
          <View style={styles.seoSection}>
            <Text size="xs" weight="bold" color="tertiary" style={styles.seoEyebrow}>
              About the catalog
            </Text>
            <Text weight="bold" color="primary" style={[styles.seoH2, isXS && { fontSize: 24, lineHeight: 30 }]}>
              {enrichedTemplates.length}+ pre-built AI workflows, indexed by what you’re shipping.
            </Text>
            <Text size="m" color="secondary" style={styles.seoLede}>
              Every recipe in this catalog is a Gab AI Deck layout — a multi-column workspace where each column runs a different AI model, agent, or one-click tool. Cloning a recipe copies the column structure, the default model bindings, and the workflow’s description into a brand-new deck under your account. You then rename, swap models, reorder columns, or save the layout as your own recipe.
            </Text>

            <View style={styles.howRow}>
              {[
                { icon: 'magnifying-glass', title: 'Browse by category',  copy: 'The left rail groups recipes by what you’re building — music video, podcast, brand kit, ad creative, ecommerce, research, code review, and more.' },
                { icon: 'wand-magic-sparkles', title: 'Clone in one click', copy: 'Hit “Use this recipe” to spawn a fresh deck. The columns, labels, and default model bindings come along for the ride.' },
                { icon: 'sliders',          title: 'Make it yours',         copy: 'Rename the deck, change column models, drag-reorder, add chat or tool columns, save the layout, and re-run it next time.' },
              ].map((p) => (
                <View key={p.title} style={[styles.howCard, { backgroundColor: colors.solid_color_block, borderColor: colors.border_color_secondary }]}>
                  <View style={[styles.howIcon, { backgroundColor: colors.solid_color_secondary }]}>
                    <Icon name={p.icon} size={14} color={colors.text_color_primary} solid />
                  </View>
                  <Text size="m" weight="bold" color="primary" style={{ marginTop: 12 }}>{p.title}</Text>
                  <Text size="n" color="secondary" style={{ marginTop: 6, lineHeight: 20 }}>{p.copy}</Text>
                </View>
              ))}
            </View>
          </View>

          <View style={styles.seoSection}>
            <Text size="xs" weight="bold" color="tertiary" style={styles.seoEyebrow}>
              How recipes are organized
            </Text>
            <Text weight="bold" color="primary" style={[styles.seoH2, isXS && { fontSize: 24, lineHeight: 30 }]}>
              From three-column sprints to full twelve-column production stacks.
            </Text>
            <Text size="m" color="secondary" style={styles.seoLede}>
              Recipes range from quick three- and four-column comparisons (perfect for a Tuesday afternoon) to seven- and eight-column pipelines that walk a script all the way through to a finished video. The columns can mix any combination of:
            </Text>
            <View style={styles.bulletGrid}>
              {[
                'Text & chat models — GPT-5.5, Claude, Gemini, DeepSeek, Qwen, Arya, o3.',
                'Image generators — Imagen 4, Flux Pro, Ideogram, Recraft, Seedream, Nano Banana.',
                'Video models — Kling, Wan, Seedance, Veo. Text-to-video and image-to-video.',
                'Music & SFX — MiniMax Music, CassetteAI sound effects.',
                'Voice & TTS — ElevenLabs, OpenAI TTS, multi-language dubbing presets.',
                'Transcription — Whisper Large, ElevenLabs Scribe v2 with diarization.',
                'Upscaling & polish — Topaz, restoration tools, background removers.',
                'Conversation columns — saved chats, agents, and one-click branded tools.',
              ].map((line) => (
                <View key={line} style={styles.bulletRow}>
                  <View style={[styles.bulletDot, { backgroundColor: colors.text_color_primary }]} />
                  <Text size="m" color="primary" style={{ flex: 1, lineHeight: 22 }}>{line}</Text>
                </View>
              ))}
            </View>
          </View>

          <View style={styles.seoSection}>
            <Text size="xs" weight="bold" color="tertiary" style={styles.seoEyebrow}>
              Who picks recipes off this page
            </Text>
            <Text weight="bold" color="primary" style={[styles.seoH2, isXS && { fontSize: 24, lineHeight: 30 }]}>
              Built for everyone who ships AI assets every week.
            </Text>
            <View style={styles.checkGrid}>
              {[
                'Solo creators producing weekly videos, podcasts, and reels',
                'Designers running brand sprints across four image models in parallel',
                'Marketing teams building ad-creative matrices across copy + visual variants',
                'Founders drafting investor decks plus presenter videos in one window',
                'Educators converting a syllabus into slides, narration, and quiz questions',
                'Indie game devs producing concept art, sprite sheets, SFX, and music loops',
                'E-commerce sellers polishing product photography, descriptions, and ads',
                'Engineers benchmarking GPT-5.5 vs Claude vs Gemini vs o3 on the same brief',
                'Researchers turning long PDFs into briefs, slides, and audio digests',
                'Anyone who would rather one window than five tabs',
              ].map((line) => (
                <View key={line} style={styles.checkRow}>
                  <View style={[styles.checkDot, { backgroundColor: 'rgba(16,185,129,0.15)' }]}>
                    <Icon name="check" size={12} color="#10B981" solid />
                  </View>
                  <Text size="m" color="primary" style={{ flex: 1, lineHeight: 22 }}>{line}</Text>
                </View>
              ))}
            </View>
          </View>

          <View style={styles.seoSection}>
            <Text size="xs" weight="bold" color="tertiary" style={styles.seoEyebrow}>
              FAQ
            </Text>
            <Text weight="bold" color="primary" style={[styles.seoH2, isXS && { fontSize: 24, lineHeight: 30 }]}>
              Common questions about Gab AI Deck recipes.
            </Text>
            <View style={{ gap: 12, marginTop: 18 }}>
              {RECIPES_INDEX_FAQ.map((item) => (
                <View key={item.q} style={[styles.faqCard, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block }]}>
                  <View style={styles.faqQRow}>
                    <Icon name="circle-question" size={15} color={colors.text_color_primary} />
                    <Text size="l" weight="bold" color="primary" style={{ flex: 1, lineHeight: 22 }}>{item.q}</Text>
                  </View>
                  <Text size="n" color="secondary" style={{ marginTop: 8, lineHeight: 22 }}>{item.a}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>
      )}
    </>
  );

  return (
    <>
      <MetaHead
        title="Free AI Deck Recipes — LLM, Image & Video Workflows | Gab AI"
        description="Browse 16+ free Gab AI Deck recipes: multi-column templates for music videos, podcasts, ads, LLM showdowns, and image model benchmarks. Each guide includes FAQs and HowTo steps—clone to your workspace in one click."
        canonical="/decks/recipes"
        keywords="Gab AI deck recipes, AI workflow template, multi-column AI, LLM comparison workflow, AI music video template, image model benchmark, podcast AI workflow, free AI deck"
        structuredData={structuredData}
        type="website"
      />

      <View style={styles.root}>
        <ScrollView
          style={styles.pageScroll}
          contentContainerStyle={styles.pageScrollContent}
          showsVerticalScrollIndicator={false}
        >
        <View style={styles.header}>
          <CatalogDiscoveryHero
            topline="Deck recipes"
            headline="Pre-built AI workflows. One click, one window."
            subtitle="Each recipe is a deck — up to 12 columns of text, image, video, music, voice, and tools running side by side."
          />
        </View>

        {showDeckCatalogChrome ? (
          <CatalogBrowseShell
            isXS={isXS}
            splitGap={0}
            splitPadding={{ paddingLeft: 0, paddingRight: 0 }}
            bodyGap={0}
            hideRail
            suppressMobilePills
            toolbar={isXS
              ? { ...deckBrowseToolbar, mobile: deckMobileToolbar }
              : deckBrowseToolbar}
          >
            <View style={styles.contentColumn}>
              {mainBody}
            </View>
          </CatalogBrowseShell>
        ) : (
          <View style={styles.contentColumn}>
            <View style={styles.gridScroll}>
              {mainBody}
            </View>
          </View>
        )}

        <View style={styles.footerWrap}>
          <AppDashboardFooter />
        </View>
        </ScrollView>
      </View>
    </>
  );
}

function createStyles(colors, isXS) {
  return StyleSheet.create({
    root: {
      flex: 1,
      backgroundColor: colors.solid_color_primary,
      width: '100%',
      alignSelf: 'stretch',
    },
    pageScroll: {
      flex: 1,
    },
    pageScrollContent: {
      flexGrow: 1,
    },
    header: {
      width: '100%',
    },
    crumbs: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginBottom: 16,
      ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
    },

    contentColumn: {
      flex: 1,
      minWidth: 0,
    },
    gridScroll: {
      padding: isXS ? 12 : 16,
      paddingBottom: 80,
    },

    loading: { marginTop: 48, alignItems: 'center' },
    errorBox: { marginTop: 24, padding: 16, borderRadius: 10, borderWidth: 1 },

    sectionLabel: { textTransform: 'uppercase', letterSpacing: 1.2, marginTop: 28, marginBottom: 14 },
    sectionLabelTight: { marginTop: 22, marginBottom: 12 },
    section: { width: '100%' },
    sectionHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      marginTop: 28,
      marginBottom: 14,
    },
    sectionHeaderRowTight: { marginTop: 22, marginBottom: 12 },
    sectionTitle: {
      flex: 1,
      minWidth: 0,
      textTransform: 'uppercase',
      letterSpacing: 1.2,
    },
    showAllBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      flexShrink: 0,
      ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
    },

    cardRow: {
      width: '100%',
      gap: 16,
      ...(Platform.OS === 'web'
        ? {}
        : { flexDirection: 'row', flexWrap: 'wrap' }),
    },
    cardRowWeb: {
      display: 'grid',
      alignItems: 'stretch',
      justifyContent: 'start',
    },
    emptyBox: {
      marginTop: 24,
      padding: 28,
      borderRadius: 10,
      borderWidth: 1,
      alignItems: 'center',
      width: '100%',
      maxWidth: 520,
      alignSelf: 'center',
    },
    emptyCta: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 14,
      paddingVertical: 8,
      borderRadius: 6,
      borderWidth: 1,
      marginTop: 16,
      ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
    },

    // ── SEO long-form sections (rendered below the card grid) ──
    seoWrap: {
      marginTop: 60,
      paddingTop: 30,
      borderTopWidth: 1,
      borderTopColor: colors.border_color_secondary,
      gap: 60,
      alignSelf: 'stretch',
    },
    seoSection: { width: '100%', maxWidth: 980, alignSelf: 'flex-start' },
    seoEyebrow: { textTransform: 'uppercase', letterSpacing: 1.4, marginBottom: 15 },
    seoH2: { fontSize: 30, lineHeight: 38, letterSpacing: -0.5, maxWidth: 760 },
    seoLede: { marginTop: 14, lineHeight: 26, maxWidth: 760 },

    howRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
      marginTop: 22,
    },
    howCard: {
      flexBasis: 280,
      flexGrow: 1,
      minWidth: 240,
      maxWidth: 380,
      padding: 16,
      borderRadius: 12,
      borderWidth: 1,
    },
    howIcon: {
      width: 30,
      height: 30,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
    },

    bulletGrid: {
      marginTop: 20,
      gap: 10,
    },
    bulletRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
    },
    bulletDot: {
      width: 8,
      height: 8,
      borderRadius: 10,
    },

    checkGrid: {
      marginTop: 20,
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 15,
    },
    checkRow: {
      flexBasis: 320,
      maxWidth: 320,
      flexGrow: 1,
      flexShrink: 1,
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 10,
    },
    checkDot: {
      width: 18,
      height: 18,
      borderRadius: 9,
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: 2,
    },

    faqCard: {
      padding: 16,
      borderRadius: 12,
      borderWidth: 1,
    },
    faqQRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 7,
      paddingTop: 2,
    },
    footerWrap: {
      paddingHorizontal: isXS ? 16 : 28,
    },
  });
}
